import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import './EventRequirementsConfig.css';
import { translateError } from '../utils/errorMessages';

interface RequirementType {
  _id: string;
  name: string;
  code: string;
  description?: string;
  inputType: 'boolean' | 'text' | 'file' | 'date' | 'select';
  options?: string[];
  templateFile?: string;
  templateFileName?: string;
  isSystem: boolean;
}

interface EventRequirement {
  _id: string;
  requirementType: RequirementType;
  isRequired: boolean;
  dueDate?: string;
  description?: string;
}

interface EventRequirementsConfigProps {
  eventId: string;
  onChange?: () => void;
}

const EventRequirementsConfig: React.FC<EventRequirementsConfigProps> = ({
  eventId,
  onChange,
}) => {
  const { t } = useTranslation();
  const [requirementTypes, setRequirementTypes] = useState<RequirementType[]>([]);
  const [eventRequirements, setEventRequirements] = useState<EventRequirement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);

  // Local state for editing
  const [editingReq, setEditingReq] = useState<string | null>(null);
  const [editDueDate, setEditDueDate] = useState<string>('');
  const [editIsRequired, setEditIsRequired] = useState<boolean>(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [typesResponse, eventReqResponse] = await Promise.all([
        apiClient.get<RequirementType[]>('/api/requirements/types'),
        apiClient.get<EventRequirement[]>(`/api/requirements/events/${eventId}/requirements`),
      ]);

      setRequirementTypes(typesResponse.data);
      setEventRequirements(eventReqResponse.data);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    if (eventId) {
      fetchData();
    }
  }, [eventId, fetchData]);

  const isRequirementEnabled = (reqTypeId: string): boolean => {
    return eventRequirements.some(er => er.requirementType._id === reqTypeId);
  };

  const getEventRequirement = (reqTypeId: string): EventRequirement | undefined => {
    return eventRequirements.find(er => er.requirementType._id === reqTypeId);
  };

  const handleToggleRequirement = async (reqType: RequirementType) => {
    const isEnabled = isRequirementEnabled(reqType._id);

    try {
      setSaving(reqType._id);
      setError(null);

      if (isEnabled) {
        // Remove requirement from event
        const eventReq = getEventRequirement(reqType._id);
        if (eventReq) {
          await apiClient.delete(`/api/requirements/events/${eventId}/requirements/${eventReq._id}`);
        }
      } else {
        // Add requirement to event
        await apiClient.post(`/api/requirements/events/${eventId}/requirements`, {
          requirementTypeId: reqType._id,
          isRequired: true,
        });
      }

      await fetchData();
      if (onChange) onChange();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSaving(null);
    }
  };

  const startEditing = (eventReq: EventRequirement) => {
    setEditingReq(eventReq._id);
    setEditDueDate(eventReq.dueDate ? new Date(eventReq.dueDate).toISOString().split('T')[0] : '');
    setEditIsRequired(eventReq.isRequired);
  };

  const cancelEditing = () => {
    setEditingReq(null);
    setEditDueDate('');
    setEditIsRequired(true);
  };

  const handleSaveEdit = async (eventReq: EventRequirement) => {
    try {
      setSaving(eventReq._id);
      setError(null);

      await apiClient.put(`/api/requirements/events/${eventId}/requirements/${eventReq._id}`, {
        isRequired: editIsRequired,
        dueDate: editDueDate || null,
      });

      await fetchData();
      setEditingReq(null);
      if (onChange) onChange();
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setSaving(null);
    }
  };

  const getInputTypeLabel = (inputType: string): string => {
    switch (inputType) {
      case 'boolean':
        return t('requirements.inputTypes.boolean');
      case 'text':
        return t('requirements.inputTypes.text');
      case 'file':
        return t('requirements.inputTypes.file');
      case 'date':
        return t('requirements.inputTypes.date');
      case 'select':
        return t('requirements.inputTypes.select');
      default:
        return inputType;
    }
  };

  if (loading) {
    return <div className="event-requirements-config loading">{t('common.loading')}</div>;
  }

  return (
    <div className="event-requirements-config">
      <div className="config-header">
        <h4>{t('requirements.configTitle')}</h4>
        <p className="config-description">{t('requirements.configDescription')}</p>
      </div>

      {error && <div className="config-error">{error}</div>}

      <div className="requirements-list">
        {requirementTypes.length === 0 ? (
          <div className="no-types">{t('requirements.noTypesAvailable')}</div>
        ) : (
          requirementTypes.map(reqType => {
            const eventReq = getEventRequirement(reqType._id);
            const isEnabled = !!eventReq;
            const isBeingEdited = editingReq === eventReq?._id;

            return (
              <div
                key={reqType._id}
                className={`requirement-config-item ${isEnabled ? 'enabled' : ''}`}
              >
                <div className="config-item-main">
                  <label className="toggle-container">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={() => handleToggleRequirement(reqType)}
                      disabled={saving === reqType._id}
                    />
                    <span className="toggle-slider"></span>
                  </label>

                  <div className="config-item-info">
                    <div className="config-item-name">
                      {reqType.name}
                      {reqType.isSystem && (
                        <span className="system-badge">{t('requirements.system')}</span>
                      )}
                    </div>
                    <div className="config-item-meta">
                      <span className="input-type-badge">
                        {getInputTypeLabel(reqType.inputType)}
                      </span>
                      {reqType.description && (
                        <span className="config-item-description">{reqType.description}</span>
                      )}
                    </div>
                  </div>

                  {isEnabled && eventReq && !isBeingEdited && (
                    <button
                      type="button"
                      className="config-edit-btn"
                      onClick={() => startEditing(eventReq)}
                      title={t('common.edit')}
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                  )}
                </div>

                {/* Expanded settings when enabled */}
                {isEnabled && eventReq && (
                  <div className="config-item-settings">
                    {isBeingEdited ? (
                      <div className="config-edit-form">
                        <div className="edit-field">
                          <label className="checkbox-inline">
                            <input
                              type="checkbox"
                              checked={editIsRequired}
                              onChange={(e) => setEditIsRequired(e.target.checked)}
                            />
                            <span>{t('requirements.isRequired')}</span>
                          </label>
                        </div>

                        <div className="edit-field">
                          <label>{t('requirements.dueDate')}</label>
                          <input
                            type="date"
                            value={editDueDate}
                            onChange={(e) => setEditDueDate(e.target.value)}
                          />
                        </div>

                        <div className="edit-actions">
                          <button
                            type="button"
                            className="save-btn"
                            onClick={() => handleSaveEdit(eventReq)}
                            disabled={saving === eventReq._id}
                          >
                            {saving === eventReq._id ? t('common.saving') : t('common.save')}
                          </button>
                          <button
                            type="button"
                            className="cancel-btn"
                            onClick={cancelEditing}
                          >
                            {t('common.cancel')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="config-status-row">
                        <span className={`required-status ${eventReq.isRequired ? 'required' : 'optional'}`}>
                          {eventReq.isRequired ? t('requirements.required') : t('requirements.optional')}
                        </span>
                        {eventReq.dueDate && (
                          <span className="due-date-display">
                            {t('requirements.due')}: {new Date(eventReq.dueDate).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default EventRequirementsConfig;
